import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react'
import { useTeamName } from '../utils'
import {
  Coach,
  EventGQL,
  fetchTeamAllDataGQL,
  fetchStandings,
  TeamGQL,
  fetchActiveBanners,
  getFeedByTag,
} from '../../Api'
import {
  CURRENT_YEAR,
  EVENTS_YEAR,
  isChampSeriesLive,
  STATS_YEAR,
} from '../../Config/League/league.config'
import {
  getAdditionalTeamData,
  getFullTeamName,
  getIdByTeamName,
  SEASON_SEGMENT_NAMES,
  checkHasChampSeriesStarted,
  filterChampSeriesStandings,
} from '../../Utils'
import { SHOP_COLLECTIONS } from '../../Config/Shop/shop.config'
import { fetchShopCollection } from '../../Api/Shop/shop.api'
import { ShopProductSortKeys } from '../../Api/Shop/shop.utils'
import { TEAMS_URL } from '../../Routing/urls'
import { STORAGE_NAMES, websiteUrls } from '../../Constant'
import { useStorageContext } from '../StorageContext'
import { initialState, TEAM_ACTIONS, teamReducer } from './reducer'
import type { TeamContextType } from './types'
import type { LeaderData } from '../../Api'

export const TeamContext = createContext<TeamContextType | null>(null)

export const TeamProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const teamNameParam = useTeamName()
  const teamId = getIdByTeamName(teamNameParam)
  const { isStorageReady, fetchAndCache } = useStorageContext()
  const [state, dispatch] = useReducer(teamReducer, initialState)
  const mountedRef = useRef(true)

  const fetchData = useCallback(async () => {
    await Promise.all([
      fetchTeamData(),
      fetchBanners(),
      fetchTeamStandings(),
      fetchFeed(),
      fetchTeamShopCollection(),
    ])
  }, [])

  useEffect(() => {
    mountedRef.current = true
    if (isStorageReady && teamId) fetchData()
    return () => {
      mountedRef.current = false
    }
  }, [teamId, isStorageReady, fetchData])

  const fetchTeamData = async () => {
    const fetcher = async (): Promise<TeamGQL> => {
      let fetchedTeam = await fetchTeamAllDataGQL(
        teamId,
        CURRENT_YEAR,
        STATS_YEAR,
        EVENTS_YEAR,
        isChampSeriesLive
      )
      if (fetchedTeam) {
        fetchedTeam = { ...getAdditionalTeamData(teamId), ...fetchedTeam }
      }
      if (!fetchedTeam) {
        throw new Error('No team data available')
      }
      return fetchedTeam
    }

    const onUpdate = (team: TeamGQL) => {
      if (!mountedRef.current) return
      if (team) {
        handleSettingTeamData(team)
      } else {
        dispatch({ type: TEAM_ACTIONS.SET_TEAM_LOADING, payload: false })
      }
    }

    try {
      await fetchAndCache<TeamGQL>(
        `${STORAGE_NAMES.team_data}_${teamId}`,
        fetcher,
        onUpdate
      )
    } catch (err: any) {
      console.log(err)
      dispatch({ type: TEAM_ACTIONS.SET_ERROR, payload: err })
      dispatch({ type: TEAM_ACTIONS.SET_TEAM_LOADING, payload: false })
    }
  }

  const handleSettingTeamData = (team: TeamGQL) => {
    structureTeamStats(team)
    structureSeasonEvents(team)

    if (team?.coaches) {
      const coaches = mapDefaultCoachImage(team.coaches, team?.fullName)
      dispatch({
        type: TEAM_ACTIONS.SET_COACHES,
        payload: coaches,
      })
    }
    dispatch({ type: TEAM_ACTIONS.SET_TEAM_DATA, payload: team })
    dispatch({ type: TEAM_ACTIONS.SET_TEAM_LOADING, payload: false })
  }

  const fetchFeed = async () => {
    try {
      const teamFeed = await getFeedByTag({ tags: [teamId] })
      if (teamFeed?.length > 0) {
        dispatch({ type: TEAM_ACTIONS.SET_TEAM_FEED, payload: teamFeed })
      }
      dispatch({ type: TEAM_ACTIONS.SET_TEAM_FEED_LOADING, payload: false })
      return
    } catch (err) {
      console.log(err)
      dispatch({ type: TEAM_ACTIONS.SET_TEAM_FEED_LOADING, payload: false })
      return
    }
  }

  const fetchBanners = async () => {
    try {
      let teamName = getFullTeamName(teamId)
      const bannerRes = await fetchActiveBanners()
      if (!bannerRes || !teamName) return
      const teamBanners = bannerRes.filter(
        (b) => b?.location && b.location.includes(teamName)
      )
      dispatch({ type: TEAM_ACTIONS.SET_BANNERS, payload: teamBanners })
    } catch (err) {
      console.log(err)
    }
  }

  const fetchTeamStandings = async () => {
    try {
      let standingArr = []
      const teams = await fetchStandings(STATS_YEAR, isChampSeriesLive)
      if (!teams) return
      standingArr = teams
      if (isChampSeriesLive) {
        standingArr = filterChampSeriesStandings(teams, STATS_YEAR)
      }
      dispatch({ type: TEAM_ACTIONS.SET_STANDINGS, payload: standingArr })
    } catch (err) {
      console.log(err)
    }
  }

  const fetchTeamShopCollection = async () => {
    const hasCollection = teamId in SHOP_COLLECTIONS
    if (!hasCollection) {
      console.warn(`No shop collection for ${teamId}`)
      Promise.resolve()
      return
    }
    try {
      const collection = await fetchShopCollection(
        SHOP_COLLECTIONS[teamId],
        ShopProductSortKeys.BEST_SELLING,
        10
      )
      if (collection?.products && collection.products.length > 0) {
        dispatch({
          type: TEAM_ACTIONS.SET_MERCH_COLLECTION,
          payload: collection.products,
        })
      }
      return
    } catch (err) {
      console.log(err)
    }
  }

  /**
   * Filters out CS events if ChampSeries has finished & sets csStarted if games have been played
   */
  const structureSeasonEvents = (tm: TeamGQL) => {
    if (!tm?.events) return
    let evs: EventGQL[] = []
    const csHasStarted = checkHasChampSeriesStarted(tm.events)
    if (!isChampSeriesLive) {
      const regSeason = tm.events.filter(
        (ev) => ev.seasonSegment !== SEASON_SEGMENT_NAMES.champseries
      )
      evs = regSeason
    }

    if (csHasStarted && tm.events.length > 0) {
      evs = tm.events
      dispatch({
        type: TEAM_ACTIONS.SET_CS_STARTED,
        payload: csHasStarted,
      })

      if (tm?.champSeries?.stats) {
        dispatch({
          type: TEAM_ACTIONS.SET_TEAM_CS_STATS,
          payload: tm?.champSeries?.stats,
        })
      }
    }

    dispatch({
      type: TEAM_ACTIONS.SET_EVENTS,
      payload: evs ?? [],
    })
    dispatch({
      type: TEAM_ACTIONS.SET_EVENTS_LOADING,
      payload: false,
    })
  }

  /**
   * Determines if should set reg season stats or champ series stats
   */
  const structureTeamStats = (tm: TeamGQL) => {
    if (!tm?.events) return
    const csHasStarted = checkHasChampSeriesStarted(tm.events)
    if (!isChampSeriesLive && tm?.stats) {
      dispatch({
        type: TEAM_ACTIONS.SET_TEAM_STATS,
        payload: tm.stats,
      })
      return
    }
    if (csHasStarted && tm?.champSeries?.stats) {
      dispatch({
        type: TEAM_ACTIONS.SET_TEAM_CS_STATS,
        payload: tm.champSeries.stats,
      })
      return
    }
  }

  const getStatLeader = (stat: string) => {
    let ldr: LeaderData | undefined
    if (
      state.csStarted &&
      state.team?.csStatLeaders &&
      state.team.csStatLeaders.length > 0
    ) {
      ldr = state.team.csStatLeaders.find((l) => l.statType === stat)
    } else {
      if (state.team?.statLeaders && state.team.statLeaders.length > 0) {
        ldr = state.team?.statLeaders.find((l) => l.statType === stat)
      }
    }
    return ldr
  }

  const urlFullStats = (): string => {
    if (!teamId) return ''
    return `${websiteUrls.fullTeamStats}/${teamId.toLowerCase()}`
  }

  const urlTeamRoster = (): string => {
    if (!state.team?.officialId) return ''
    return `${TEAMS_URL}/${state.team.officialId}/roster`
  }

  const urlTickets = (): string => {
    return websiteUrls.schedule
  }

  const urlShopGear = (): string => {
    if (!state.team?.fullName) return ''
    return `${websiteUrls.shopBaseUrl}/collections/${state.team.fullName}`
  }

  const getHeadCoach = (): Coach | null => {
    if (state.coaches.length < 1) return null
    const heads = state.coaches.filter((c) => c.coachType.toLowerCase() === 'head')
    if (heads.length > 0) {
      return heads[0]
    }
    return null
  }

  const getAssistantCoaches = (): Coach[] | [] => {
    if (state.coaches.length < 1) return []
    return state.coaches.filter((c) => c.coachType.toLowerCase() === 'assistant')
  }

  const mapDefaultCoachImage = (coaches: Coach[], teamName: string): Coach[] => {
    if (!teamName) return coaches
    return coaches.map((coach) => {
      if (!coach.profileUrl) {
        if (teamName !== 'Cannons') {
          coach.profileUrl = `https://premierlacrosseleague.com/wp-content/uploads/2020/02/${teamName}_nophoto.jpg`
        } else {
          coach.profileUrl = `https://premierlacrosseleague.com/wp-content/uploads/2021/04/Headshot_generic.jpg`
        }
      }
      return coach
    })
  }
  const contextValue = useMemo(
    () => ({
      teamId: teamId,
      team: state.team,
      teamLoading: state.teamLoading,
      stats: state.stats,
      csStats: state.csStats,
      events: state.events,
      eventsLoading: state.eventsLoading,
      feedLoading: state.feedLoading,
      teamFeed: state.teamFeed,
      coaches: state.coaches,
      banners: state.banners,
      standings: state.standings,
      teamMerchCollection: state.teamMerchCollection,
      csStarted: state.csStarted,
      error: state.error,
      urlFullStats,
      urlTeamRoster,
      urlShopGear,
      urlTickets,
      getStatLeader,
      getHeadCoach,
      getAssistantCoaches,
    }),
    [
      teamId,
      state.team,
      state.teamLoading,
      state.stats,
      state.csStats,
      state.events,
      state.eventsLoading,
      state.feedLoading,
      state.teamFeed,
      state.coaches,
      state.banners,
      state.standings,
      state.teamMerchCollection,
      state.csStarted,
      state.error,
      urlFullStats,
      urlTeamRoster,
      urlShopGear,
      urlTickets,
      getStatLeader,
      getHeadCoach,
      getAssistantCoaches,
    ]
  )

  return <TeamContext.Provider value={contextValue}>{children}</TeamContext.Provider>
}

export const useTeamContext = (): TeamContextType => {
  try {
    const context = useContext(TeamContext)
    if (!context) {
      throw new Error(
        'useTeamContext must be used within TeamProvider. Make sure your component is wrapped with TeamProvider.'
      )
    }
    return context
  } catch (error) {
    console.error('Error in useTeamContext:', error)
    throw error
  }
}
